import axios, { AxiosInstance, AxiosResponse } from "axios"

export type ChannelType = "email" | "app" | "web"

export type SubscriptionList = {
  id: string
  name: string
  description?: string
  subscribed: boolean
}

export interface SubscriptonFormClientInterface {
  getLists(): Promise<SubscriptionList[]>
  updateLists(lists: SubscriptionList[]): Promise<void>
}

export type ChannelLookupResult = {
  ok: boolean
  channel: {
    channel_id: string
    device_type: string
    subscription_lists: string[]
  }
}

type SubscriptionListOperation = {
  action: "subscribe" | "unsubscribe"
  list_id: string
  timestamp?: string
}

type SubscriptionListOperationRequest = {
  subscription_lists: SubscriptionListOperation[]
  audience: {
    channel: string
  }
}

type SubscriptionListListingLegacyObject = {
  list_id: string
  name: string
  description?: string
  channel_types: ChannelType[]
}

type SubscriptionListListingObject = {
  list_id: string
  name: string
  description?: string
  scopes: ChannelType[]
}

type SubscriptionListListingResponseObject =
  | SubscriptionListListingLegacyObject
  | SubscriptionListListingObject

function isSubscriptionListListingObject(
  val: SubscriptionListListingResponseObject
): val is SubscriptionListListingObject {
  return Object.prototype.hasOwnProperty.call(val, "scopes")
}

function transformLegacySubscriptionListObject(
  val: SubscriptionListListingResponseObject
): SubscriptionListListingObject {
  if (isSubscriptionListListingObject(val)) {
    return val
  }

  return {
    list_id: val.list_id,
    name: val.name,
    description: val.description,
    scopes: val.channel_types,
  }
}

export type SubscriptionListListingResult = {
  ok: boolean
  subscription_lists: SubscriptionListListingResponseObject[]
}

export class SubscriptionFormClient implements SubscriptonFormClientInterface {
  channelId: string
  requester: AxiosInstance

  constructor(channelId: string, token: string) {
    if (!token.length) throw new Error("Invalid auth provided")
    if (!channelId.length) throw new Error("Invalid channel ID provided")
    this.channelId = channelId
    this.requester = axios.create({
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/vnd.urbanairship+json; version=3;",
      },
    })
  }

  async getLists(): Promise<SubscriptionList[]> {
    const lists = await this.fetchAllLists()
    const channelLists = await this.fetchChannelLists()

    return lists
      .map(transformLegacySubscriptionListObject)
      .filter((list) => list.scopes.includes("email"))
      .map((list) => ({
        id: list.list_id,
        name: list.name,
        description: list.description,
        subscribed: channelLists.includes(list.list_id),
      }))
  }

  async updateLists(
    lists: Pick<SubscriptionList, "id" | "subscribed">[]
  ): Promise<void> {
    const request: SubscriptionListOperationRequest = {
      subscription_lists: lists.map((list) => ({
        action: list.subscribed ? "subscribe" : "unsubscribe",
        list_id: list.id,
      })),
      audience: {
        channel: this.channelId,
      },
    }
    const updateResponse = await this.requester.post(
      "/api/channels/subscription_lists",
      request
    )
    throwIfNotSuccess(updateResponse)
  }

  private async fetchAllLists(): Promise<
    SubscriptionListListingResponseObject[]
  > {
    const listResponse = await this.requester.get<SubscriptionListListingResult>(
      "/api/subscription_lists"
    )
    throwIfNotSuccess(listResponse)
    if (!listResponse.data.ok)
      throw new Error("Subscription list lookup failed")
    return listResponse.data.subscription_lists
  }

  private async fetchChannelLists(): Promise<string[]> {
    const channelResponse = await this.requester.get<ChannelLookupResult>(
      `/api/channels/${this.channelId}`
    )
    throwIfNotSuccess(channelResponse)
    if (!channelResponse.data.ok) throw new Error("Channel lookup failed")
    return channelResponse.data.channel.subscription_lists ?? []
  }
}

export function throwIfNotSuccess(response: AxiosResponse): void {
  if (response.status < 200 || response.status >= 400) {
    throw new Error(`${response.request?.url}: ${response.statusText}`)
  }
}
